(function () {
  if (customElements.get('aov380can-offer')) return;

  customElements.define('aov380can-offer', class Aov380CanOffer extends HTMLElement {
    constructor() {
      super();
      this.state = {
        variant: null,
        busy: false
      };
      this.redirectUrl = this.normalizeRedirectUrl(this.dataset.redirectUrl);
      this.primaryCta = this.dataset.primaryCta || 'Add Range Can';
    }

    connectedCallback() {
      this.bindPackages();
      this.bindButtons();
      this.bindFaq();
      this.bindCarousel();
      this.initSticky();
      this.syncInitialState();
      this.updateView();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.finalCtaObserver?.disconnect();
    }

    bindPackages() {
      this.querySelectorAll('[data-aov380-package]').forEach((option) => {
        option.addEventListener('click', () => this.selectPackage(option));
        option.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          option.click();
        });
      });
    }

    bindButtons() {
      this.querySelectorAll('[data-aov380-add], [data-aov380-sticky-add]').forEach((button) => {
        button.addEventListener('click', () => this.addToCart());
      });
    }

    bindFaq() {
      this.querySelectorAll('.aov380can-faq details').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          this.querySelectorAll('.aov380can-faq details').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        });
      });
    }

    bindCarousel() {
      this.carousel = this.querySelector('[data-aov380-carousel]');
      if (!this.carousel) return;

      this.carouselSlides = Array.from(this.carousel.querySelectorAll('[data-aov380-slide]'));
      this.carouselThumbs = Array.from(this.carousel.querySelectorAll('[data-aov380-carousel-thumb]'));
      this.carouselIndex = this.carouselSlides.findIndex((slide) => slide.classList.contains('is-active'));
      if (this.carouselIndex < 0) this.carouselIndex = 0;

      const previous = this.carousel.querySelector('[data-aov380-carousel-prev]');
      const next = this.carousel.querySelector('[data-aov380-carousel-next]');

      if (previous) {
        previous.addEventListener('click', () => this.setCarouselSlide(this.carouselIndex - 1));
      }

      if (next) {
        next.addEventListener('click', () => this.setCarouselSlide(this.carouselIndex + 1));
      }

      this.carouselThumbs.forEach((thumb, fallbackIndex) => {
        thumb.addEventListener('click', () => {
          const thumbIndex = Number(thumb.dataset.carouselIndex);
          this.setCarouselSlide(Number.isNaN(thumbIndex) ? fallbackIndex : thumbIndex);
        });
      });

      this.carousel.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.setCarouselSlide(this.carouselIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      this.setCarouselSlide(this.carouselIndex);
    }

    initSticky() {
      this.sticky = this.querySelector('[data-aov380-sticky]');
      const target = this.querySelector('.aov380can-buy');
      const finalCta = this.querySelector('.aov380can-final [data-aov380-add]');
      if (!this.sticky || !target || !('IntersectionObserver' in window)) return;

      this.stickyState = {
        buyPanelPassed: false,
        finalCtaFullyVisible: false
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.stickyState.buyPanelPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        });
        this.updateStickyVisibility();
      }, { threshold: 0, rootMargin: '0px' });

      this.observer.observe(target);

      if (finalCta) {
        this.finalCtaObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.stickyState.finalCtaFullyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.98;
          });
          this.updateStickyVisibility();
        }, { threshold: [0, 0.98, 1], rootMargin: '0px' });

        this.finalCtaObserver.observe(finalCta);
      }
    }

    updateStickyVisibility() {
      if (!this.sticky || !this.stickyState) return;
      const show = this.stickyState.buyPanelPassed && !this.stickyState.finalCtaFullyVisible;
      this.sticky.classList.toggle('is-visible', show);
    }

    syncInitialState() {
      const params = new URLSearchParams(window.location.search);
      const preferred = params.has('variant') ? null : this.querySelector('[data-aov380-package].aov380can-package--featured:not(:disabled)');
      const selected = preferred ||
        this.querySelector('[data-aov380-package].is-selected:not(:disabled)') ||
        this.querySelector('[data-aov380-package]:not(:disabled)');
      if (!selected) return;

      this.readPackage(selected);
      this.paintPackages(selected);
      this.selectPackageMedia(selected);
    }

    selectPackage(option) {
      if (!option || option.disabled || this.state.busy) return;
      this.readPackage(option);
      this.paintPackages(option);
      this.selectPackageMedia(option);
      this.updateView();
    }

    readPackage(option) {
      this.state.variant = {
        id: option.dataset.variantId || '',
        title: option.dataset.title || option.dataset.variantTitle || '',
        variantTitle: option.dataset.variantTitle || option.dataset.title || '',
        rounds: option.dataset.rounds || '',
        short: option.dataset.short || '',
        contents: option.dataset.contents || '',
        bonus: option.dataset.bonus || '',
        badge: option.dataset.badge || '',
        priceFormatted: option.dataset.priceFormatted || '',
        gift9mmVariantId: option.dataset.gift9mmVariantId || option.dataset.giftVariantId || '',
        gift9mmTitle: option.dataset.gift9mmTitle || option.dataset.giftTitle || '',
        giftBundleVariantId: option.dataset.giftBundleVariantId || option.dataset.gift9mmVariantId || option.dataset.giftVariantId || '',
        giftBundleTitle: option.dataset.giftBundleTitle || option.dataset.gift9mmTitle || option.dataset.giftTitle || '',
        giftTargetsVariantId: option.dataset.giftTargetsVariantId || '',
        giftTargetsTitle: option.dataset.giftTargetsTitle || '',
        giftCanVariantId: option.dataset.giftCanVariantId || '',
        giftCanTitle: option.dataset.giftCanTitle || '',
        giftTier: option.dataset.giftTier || '',
        bonusValue: option.dataset.bonusValue || '',
        nextUnlockMessage: option.dataset.nextUnlockMessage || '',
        nextUnlockState: option.dataset.nextUnlockState || '',
        statRoundsValue: option.dataset.statRoundsValue || '',
        stat9mmValue: option.getAttribute('data-stat-9mm-value') || '',
        statTargetsValue: option.dataset.statTargetsValue || '',
        statCanValue: option.dataset.statCanValue || '',
        faqBonusCopy: option.dataset.faqBonusCopy || '',
        available: option.dataset.available === 'true'
      };
    }

    paintPackages(selected) {
      this.querySelectorAll('[data-aov380-package]').forEach((option) => {
        const active = option === selected && !option.disabled;
        option.classList.toggle('is-selected', active);
        option.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }

    selectPackageMedia(option) {
      if (!option) return;
      if (this.carousel) {
        this.selectCarouselMedia(option.dataset.featuredMediaId);
        return;
      }

      const picture = this.querySelector('[data-aov380-media-picture]');
      const desktopImage = this.querySelector('[data-aov380-media-desktop]');
      const mobileSource = this.querySelector('[data-aov380-media-mobile]');
      const placeholder = this.querySelector('[data-aov380-media-placeholder]');
      const desktopSrc = option.dataset.imageDesktopSrc || '';
      const mobileSrc = option.dataset.imageMobileSrc || desktopSrc;
      const label = option.dataset.imageLabel || option.dataset.title || 'Selected pack';
      const alt = option.dataset.imageAlt || label;

      if (desktopImage && desktopSrc) {
        desktopImage.src = desktopSrc;
        desktopImage.alt = alt;
      }

      if (mobileSource) {
        if (mobileSrc) {
          mobileSource.srcset = mobileSrc;
        } else {
          mobileSource.removeAttribute('srcset');
        }
      }

      const hasImage = Boolean(desktopSrc);
      if (picture) picture.hidden = !hasImage;
      if (placeholder) {
        const title = placeholder.querySelector('[data-placeholder-title]');
        const copy = placeholder.querySelector('[data-placeholder-copy]');
        placeholder.hidden = hasImage;
        if (title) title.textContent = `${label} image slot`;
        if (copy) copy.textContent = option.dataset.imagePlaceholder || 'Upload this pack image in the section settings.';
      }
    }

    setCarouselSlide(index) {
      if (!this.carouselSlides || !this.carouselSlides.length) return;

      const slideCount = this.carouselSlides.length;
      const nextIndex = (index + slideCount) % slideCount;
      this.carouselIndex = nextIndex;

      this.carouselSlides.forEach((slide, fallbackIndex) => {
        const slideIndex = Number(slide.dataset.carouselIndex);
        const active = (Number.isNaN(slideIndex) ? fallbackIndex : slideIndex) === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.carouselThumbs.forEach((thumb, fallbackIndex) => {
        const thumbIndex = Number(thumb.dataset.carouselIndex);
        const active = (Number.isNaN(thumbIndex) ? fallbackIndex : thumbIndex) === nextIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    selectCarouselMedia(mediaId) {
      if (!mediaId || !this.carouselSlides || !this.carouselSlides.length) return;

      const slide = this.carouselSlides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide) return;

      const slideIndex = Number(slide.dataset.carouselIndex);
      this.setCarouselSlide(Number.isNaN(slideIndex) ? this.carouselSlides.indexOf(slide) : slideIndex);
    }

    updateView() {
      const variant = this.state.variant || {};
      const price = variant.priceFormatted || '';
      const cta = this.getCtaLabel(variant);

      this.setText('[data-aov380-summary-title]', variant.title || 'Range Can');
      this.setText('[data-aov380-summary-contents]', variant.contents || '');
      this.setText('[data-aov380-summary-bonus]', variant.bonus || '');
      this.setText('[data-aov380-summary-price]', price);
      this.setText('[data-aov380-editorial-bonus]', this.formatEditorialBonus(variant));
      this.setText('[data-aov380-action-bonus-value]', this.formatBonusValue(variant));
      this.setActionUnlock(variant);
      this.setText('[data-aov380-unlock-value]', this.formatBonusValue(variant));
      this.setText('[data-aov380-cta]', cta);
      this.setText('[data-aov380-sticky-title]', variant.title || 'Range Can');
      this.setText('[data-aov380-sticky-contents]', variant.short || '');
      this.setStickyBonus(variant);
      this.setText('[data-aov380-sticky-price]', price);
      this.setText('[data-aov380-sticky-cta]', this.getStickyCtaLabel(variant));
      this.setText('[data-aov380-unlock-summary]', this.formatBonusHeadline(variant));
      this.setText('[data-aov380-ship-rounds]', this.formatRoundLine(variant.statRoundsValue, 'rounds of .380 ACP'));
      this.setText('[data-aov380-ship-9mm]', this.formatRoundLine(variant.stat9mmValue, 'free 9mm rounds'));
      this.setText('[data-aov380-ship-targets]', this.formatSplatterTargetLine(variant.statTargetsValue));
      this.setText('[data-aov380-ship-can]', this.formatCanLine(variant.statCanValue));
      this.setText('[data-aov380-ship-pack]', variant.short || '');
      this.setText('[data-aov380-faq-bonus]', variant.faqBonusCopy || '');
      this.updateUnlockRail(variant.giftTier || '');
      this.updateSpecGrid(variant.giftTier || '');

      const unavailable = !variant.id || !variant.available || this.state.busy;
      this.querySelectorAll('[data-aov380-add], [data-aov380-sticky-add]').forEach((button) => {
        button.disabled = unavailable;
        button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      });
    }

    updateSpecGrid(tier) {
      const grid = this.querySelector('[data-aov380-spec-grid]');
      if (!grid) return;

      if (['starter', 'range', 'stock'].includes(tier)) {
        grid.dataset.selectedTier = tier;
      } else {
        grid.removeAttribute('data-selected-tier');
      }
    }

    updateUnlockRail(tier) {
      const order = ['starter', 'range', 'stock'];
      const activeIndex = Math.max(0, order.indexOf(tier));
      const rail = this.querySelector('[data-aov380-unlock]');
      if (rail) {
        rail.classList.remove('aov380can-unlock--starter', 'aov380can-unlock--range', 'aov380can-unlock--stock');
        if (order.includes(tier)) rail.classList.add(`aov380can-unlock--${tier}`);
      }

      this.querySelectorAll('[data-aov380-unlock-step]').forEach((step) => {
        const stepIndex = order.indexOf(step.dataset.tier || '');
        step.classList.toggle('is-active', stepIndex === activeIndex);
        step.classList.toggle('is-unlocked', stepIndex > -1 && stepIndex <= activeIndex);
      });
    }

    formatRoundLine(value, suffix) {
      return value ? `${value} ${suffix}` : '';
    }

    formatTargetLine(value) {
      const count = value || '0';
      const label = Number(count) === 1 ? 'target' : 'targets';
      return `${count} ${label}`;
    }

    formatSplatterTargetLine(value) {
      const count = value || '0';
      const label = Number(count) === 1 ? 'splatter target' : 'splatter targets';
      return `${count} ${label}`;
    }

    formatBonusHeadline(variant) {
      const rounds = variant.stat9mmValue ? `${variant.stat9mmValue} rounds of 9mm` : '';
      const targets = variant.statTargetsValue ? this.formatTargetLine(variant.statTargetsValue) : '';
      return [rounds, targets].filter(Boolean).join(' + ');
    }

    formatEditorialBonus(variant) {
      const title = variant.title || 'This pack';
      const rounds = this.formatRoundLine(variant.stat9mmValue, 'rounds of 9mm');
      const targets = this.formatSplatterTargetLine(variant.statTargetsValue);
      return `Every pack includes the We The People Ammo Can. ${title} adds ${rounds} and ${targets} free.`;
    }

    formatCanLine(value) {
      const count = value || '1';
      const label = Number(count) === 1 ? 'We The People Ammo Can' : 'We The People Ammo Cans';
      return `${count} ${label}`;
    }

    formatBonusValue(variant) {
      return variant.bonusValue ? `${variant.bonusValue} free-bonus value` : '';
    }

    setActionUnlock(variant) {
      this.querySelectorAll('[data-aov380-action-unlock]').forEach((element) => {
        const message = variant.nextUnlockMessage || '';
        element.textContent = message;
        element.hidden = !message;
        element.classList.toggle('is-maxed', variant.nextUnlockState === 'max');
      });
    }

    getCtaLabel(variant) {
      const label = variant.title || 'Range Can';
      return this.primaryCta.replace('[package]', label);
    }

    getStickyCtaLabel(variant) {
      return this.getCtaLabel(variant || {});
    }

    async addToCart() {
      const variant = this.state.variant;
      if (!variant || this.state.busy || !variant.id || !variant.available) return;

      const item = {
        id: Number(variant.id),
        quantity: 1,
        properties: {
          _aov_offer: '380-acp-aov-can',
          _aov_selected_package: variant.variantTitle || variant.title || ''
        }
      };

      this.setBusy(true);
      this.showError('');

      try {
        const items = [item];
        const giftItems = this.getGiftCartItems(variant);
        const missingGiftTypes = this.getMissingGiftTypes(variant);
        if (missingGiftTypes.length) {
          console.warn('[380 ACP Range Can] Missing gift variant IDs:', missingGiftTypes, variant);
          throw new Error('This range can is temporarily unavailable while we connect the included 9mm, targets, and ammo can.');
        }
        items.push(...giftItems);

        const insuranceItem = await this.getInsuranceCartItem();
        if (insuranceItem) items.push(insuranceItem);

        const response = await fetch(`${this.getRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Unable to add this range can.');
        }

        window.SubscriptionInsuranceCore?.clearCartCache?.();
        window.location.href = this.redirectUrl;
      } catch (error) {
        this.showError(error.message || 'Unable to add this range can.');
        this.setBusy(false);
      }
    }

    getGiftProperties(variant, giftType) {
      const giftProperties = {
        _free_gift: 'true',
        _aov_offer: '380-acp-aov-can',
        _aov_gift_type: giftType,
        _aov_gift_tier: variant.giftTier || '',
        _aov_parent_variant_id: String(variant.id)
      };

      if (this.dataset.productId) {
        giftProperties._aov_parent_product_id = String(this.dataset.productId);
      }

      return giftProperties;
    }

    getGiftCartItems(variant) {
      const giftItems = [];
      const giftBundleVariantId = Number(variant.giftBundleVariantId || variant.gift9mmVariantId);
      const giftCanVariantId = Number(variant.giftCanVariantId);

      if (giftBundleVariantId) {
        giftItems.push({
          id: giftBundleVariantId,
          quantity: 1,
          properties: this.getGiftProperties(variant, '9mm-targets')
        });
      }

      if (giftCanVariantId) {
        giftItems.push({
          id: giftCanVariantId,
          quantity: 1,
          properties: this.getGiftProperties(variant, 'we-the-people-ammo-can')
        });
      }

      return giftItems;
    }

    getMissingGiftTypes(variant) {
      const missing = [];
      if (!Number(variant.giftBundleVariantId || variant.gift9mmVariantId)) missing.push('9mm-targets-bundle');
      if (!Number(variant.giftCanVariantId)) missing.push('ammo-can');
      return missing;
    }

    async getInsuranceCartItem() {
      try {
        if (window.SubscriptionInsuranceCore) {
          const insuranceAction = await window.SubscriptionInsuranceCore.determineInsuranceAction(false, null);
          return await window.SubscriptionInsuranceCore.executeInsuranceAction(insuranceAction);
        }
      } catch (error) {
        console.warn('[380 ACP Range Can] Insurance check failed, using fallback:', error);
      }

      const settings = window.theme?.shopSettings || {};
      const variantId = Number(settings.shippingInsuranceVariantId);
      if (!settings.shippingInsuranceEnabled || !variantId) return null;

      return {
        id: variantId,
        quantity: 1
      };
    }

    getRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    normalizeRedirectUrl(url) {
      const redirectUrl = (url || '/checkout').trim();
      if (!redirectUrl || /^\/?cart\/?$/i.test(redirectUrl)) return '/checkout';
      return redirectUrl;
    }

    setBusy(isBusy) {
      this.state.busy = isBusy;
      this.classList.toggle('is-busy', isBusy);
      this.updateView();
    }

    showError(message) {
      const error = this.querySelector('[data-aov380-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value || '';
      });
    }

    setStickyBonus(variant) {
      const lines = [
        variant.stat9mmValue ? `${variant.stat9mmValue} rounds 9mm` : '',
        variant.statTargetsValue ? `${variant.statTargetsValue} targets + ammo can` : 'Ammo can included'
      ].filter(Boolean);

      this.querySelectorAll('[data-aov380-sticky-bonus]').forEach((element) => {
        element.textContent = '';
        lines.forEach((line) => {
          const item = document.createElement('span');
          item.textContent = line;
          element.appendChild(item);
        });
      });
    }
  });
})();
